import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DiskService } from '../../services/disk/disk.service';
import { DeltaService, EntityNames } from '../../data-services/delta/delta.service';
import { DynamicsClientService } from '../../data-services/dynamics-client/dynamics-client.service';
import { DateTimeFormatsService } from '../../services/date-time-formats/date-time-formats.service';
import { DB_KEY_PREFIXES } from '../../config/pouch-db.config';
import { OfflineServiceBase } from '../../services/offline/offline.service';
import { AgentConfig, AgentCategory, AgentHost, AgentSkill, toSkillCommand } from '../models/agent-config.model';
import { AuthenticationService } from '../../services/authentication.service';
import { FeatureActionsMap } from '../../classes/authentication/user.class';

/** Raw `indskr_agentconfiguration` row as returned by Dynamics. */
interface RawAgentConfig {
  _id?: string;
  _rev?: string;
  indskr_agentconfigurationid: string;
  indskr_name: string;
  indskr_agentname?: string | null;
  indskr_endpoint: string;
  indskr_agentcategory: AgentCategory;
  indskr_agenthost: AgentHost;
  statecode?: number;
  statuscode?: number;
  indskr_groundingconfig?: string | null;
  indskr_instructions?: string | null;
  indskr_model?: string | null;
  indskr_additionalinstructions?: string | null;
  indskr_dataversemcpserverid?: string | null;
  /**
   * Security-role ids bound via indskr_securityroleassignment (synthetic, assembled at
   * fetch). No underscore prefix: PouchDB rejects custom `_*` members at the top level.
   */
  boundRoleIds?: string[] | null;
  /**
   * Skills bound via the indskr_agentskill_indskr_agentconfiguration N:N (synthetic,
   * assembled at fetch). Same no-underscore rule as boundRoleIds.
   */
  skills?: RawAgentSkill[] | null;
}

/** The stored slice of an `indskr_agentskill` row. */
interface RawAgentSkill {
  indskr_agentskillid: string;
  indskr_name: string;
  indskr_description: string | null;
  indskr_keywords: string | null;
  indskr_sortorder: number | null;
  indskr_showinmenu: boolean | null;
}

/** An `indskr_agentskill` row as fetched, carrying the config it is bound to. */
interface RawSkillBinding extends Partial<RawAgentSkill> {
  indskr_agentskillid: string;
  'bind.indskr_agentconfigurationid'?: string;
}

/**
 * `statuscode` (Status Reason) marking an agent configuration as Published. Only
 * Published configs are synced to the device and served; any other reason (Draft,
 * inactive, …) is treated like a deletion and removed from local storage.
 */
const PUBLISHED_STATUSCODE = 268890001;

/**
 * Wrap entity-body lines in the standard no-lock fetch envelope, so each query below reads as
 * just the columns and joins it actually cares about.
 */
function fetchXml(entity: string, body: string[]): string {
  return [
    '<fetch version="1.0" output-format="xml-platform" mapping="logical" no-lock="true">',
    `<entity name="${entity}">`,
    ...body,
    '</entity>',
    '</fetch>',
  ].join('');
}

/**
 * Offline-backed registry of AI agent configurations sourced from the Dataverse
 * entity `indskr_agentconfiguration`. Extends OfflineServiceBase, so the sync
 * lifecycle drives incremental fetch + PouchDB persistence; consumers read the
 * cached `configs$` stream (works offline once synced).
 */
@Injectable({ providedIn: 'root' })
export class AgentConfigService extends OfflineServiceBase<RawAgentConfig, 'indskr_agentconfigurationid'> {
  /** Mapped, app-facing configs derived from the cached raw disk stream. */
  readonly configs$: Observable<AgentConfig[]> = this.raw$.pipe(
    map(rows => rows.map(AgentConfigService.toAgentConfig)),
  );
  readonly #dynamics = inject(DynamicsClientService);
  readonly #auth = inject(AuthenticationService);

  constructor(
    disk: DiskService,
    deltaService: DeltaService,
    dateTimeFormatsService: DateTimeFormatsService,
  ) {
    super(
      disk,
      deltaService,
      EntityNames.agentConfiguration,
      'indskr_agentconfigurationid',
      DB_KEY_PREFIXES.AGENT_CONFIG,
      dateTimeFormatsService,
    );
  }

  /**
   * Skip the agent-config sync entirely for reps with no AI entitlement — there is
   * no agent surface to serve, so pulling the configs (and their role assignments)
   * is dead weight, and it keeps getAgent()/isAvailable() honestly null for them.
   * Mirrors the rxsalesdata/bricks pattern (isSyncAllowed → hasFeatureAction). Keep
   * this OR-set aligned with the category→surface→FA map: insights (2/3/4) +
   * assistant (5) → NOVA_ASSISTANT; meeting (1) → NOVA_TRANSCRIPT_ANALYSIS; briefs
   * (6/7/8) → CONTACT/ACCOUNT_AI_SUMMARY; general-purpose (9) → VOICE_NOTE_TAKER.
   * A new AI surface means extending this set.
   */
  protected override isSyncAllowed(): boolean {
    return this.#auth.hasFeatureAction(FeatureActionsMap.NOVA_ASSISTANT)
      || this.#auth.hasFeatureAction(FeatureActionsMap.NOVA_TRANSCRIPT_ANALYSIS)
      || this.#auth.hasFeatureAction(FeatureActionsMap.CONTACT_AI_SUMMARY)
      || this.#auth.hasFeatureAction(FeatureActionsMap.ACCOUNT_AI_SUMMARY)
      || this.#auth.hasFeatureAction(FeatureActionsMap.VOICE_NOTE_TAKER);
  }

  /**
   * Full-table fetch on every sync (the incremental timestamp is deliberately ignored):
   * the table is a handful of rows, and the security-role assignments live on a SEPARATE
   * entity — they can change without touching the config row's modifiedon, so a delta on
   * configs alone would serve stale role bindings forever.
   */
  public async fetchData(_lastUpdatedTime: string) {
    const fetchXML = fetchXml('indskr_agentconfiguration', [
      '<attribute name="indskr_agentconfigurationid" />',
      '<attribute name="indskr_name" />',
      '<attribute name="indskr_agentname" />',
      '<attribute name="indskr_endpoint" />',
      '<attribute name="indskr_agentcategory" />',
      '<attribute name="indskr_agenthost" />',
      // Configurable-summaries seams (OMNI-59951). indskr_groundingconfig holds the structured
      // grounding JSON the PCF writes; its kind:'device' sources drive deterministic on-device RAG
      // (AiDataSourceRegistry.resolve). indskr_additionalinstructions is injected centrally
      // by the transport as a first-turn system message and applies to every agent. Both columns
      // exist on indskr_agentconfiguration in dev / qa / addonqa.
      '<attribute name="indskr_groundingconfig" />',
      '<attribute name="indskr_instructions" />',
      '<attribute name="indskr_model" />',
      '<attribute name="indskr_additionalinstructions" />',
      '<attribute name="indskr_dataversemcpserverid" />',
      '<attribute name="statecode" />',
      '<attribute name="statuscode" />',
    ]);

    const [records, rolesByConfig, skillsByConfig] = await Promise.all([
      this.#dynamics.executeFetchQuery<RawAgentConfig>('indskr_agentconfigurations', fetchXML),
      this.#fetchRoleAssignments(),
      this.#fetchSkills(),
    ]);

    return (records ?? []).map(item => {
      // Only Active + Published configs are served. Anything else — inactive, or an
      // unpublished reason (Draft, …) — is removed from local storage, so unpublishing
      // a config takes it off the device on the next sync.
      if (item.statecode !== 0 || item.statuscode !== PUBLISHED_STATUSCODE) {
        return { indskr_agentconfigurationid: item.indskr_agentconfigurationid, _deleted: true };
      }
      // FetchXML omits null attributes and the base sync merges into the existing local
      // doc, so each clearable config field must be stated explicitly on every row —
      // otherwise a value cleared in Dynamics would survive locally forever. Same rule
      // for boundRoleIds and skills: always set (possibly []) so removed bindings clear locally.
      return {
        ...item,
        skills: skillsByConfig.get(item.indskr_agentconfigurationid) ?? [],
        indskr_agentname: item.indskr_agentname ?? null,
        indskr_groundingconfig: item.indskr_groundingconfig ?? null,
        indskr_instructions: item.indskr_instructions ?? null,
        indskr_model: item.indskr_model ?? null,
        indskr_additionalinstructions: item.indskr_additionalinstructions ?? null,
        indskr_dataversemcpserverid: item.indskr_dataversemcpserverid ?? null,
        boundRoleIds: rolesByConfig.get(item.indskr_agentconfigurationid) ?? [],
      };
    });
  }

  /**
   * Security-role bindings: `indskr_securityroleassignment` rows whose polymorphic
   * `indskr_target` points at an agent config. A config bound to roles is only served
   * to users holding one of them (AgentRegistryService.getAgent); a config with no
   * bindings is the category default for everyone.
   */
  async #fetchRoleAssignments(): Promise<Map<string, string[]>> {
    const fetchXML = fetchXml('indskr_securityroleassignment', [
      '<attribute name="indskr_role" />',
      '<attribute name="indskr_target" />',
      '<filter type="and">',
      '<condition attribute="statecode" operator="eq" value="0" />',
      '</filter>',
      // Inner-join to agent configs: scopes the polymorphic target to THIS entity only.
      '<link-entity name="indskr_agentconfiguration" from="indskr_agentconfigurationid" to="indskr_target" link-type="inner" />',
    ]);

    const map = new Map<string, string[]>();
    try {
      const rows = await this.#dynamics.executeFetchQuery<{
        _indskr_role_value?: string;
        _indskr_target_value?: string;
      }>('indskr_securityroleassignments', fetchXML);
      for (const r of rows ?? []) {
        if (!r._indskr_role_value || !r._indskr_target_value) continue;
        const list = map.get(r._indskr_target_value) ?? [];
        list.push(r._indskr_role_value);
        map.set(r._indskr_target_value, list);
      }
    } catch (e) {
      // Orgs without the agent-config → role-assignment relationship reject the join.
      // Treat as "no bindings" so config sync keeps working there.
      console.warn('AgentConfig role-assignment fetch failed; treating all configs as unrestricted:', e);
    }
    return map;
  }

  /**
   * Skills bound to an agent config through the indskr_agentskill_indskr_agentconfiguration N:N.
   * The inner join over the intersect is the scope: only skills actually bound to a config come
   * down, and each row carries the config it is bound to, so one query yields the whole mapping.
   * A skill bound to several configs arrives once per binding — that duplication IS the mapping.
   *
   * indskr_body is deliberately not selected. The grounding MCP resolves skill bodies server-side
   * from the agentConfigId the transport sends, so the device only needs the catalog entry.
   */
  async #fetchSkills(): Promise<Map<string, RawAgentSkill[]>> {
    const map = new Map<string, RawAgentSkill[]>();
    try {
      const rows = await this.#dynamics.executeFetchQuery<RawSkillBinding>(
        'indskr_agentskills',
        fetchXml('indskr_agentskill', [
          '<attribute name="indskr_agentskillid" />',
          '<attribute name="indskr_name" />',
          '<attribute name="indskr_description" />',
          '<attribute name="indskr_keywords" />',
          '<attribute name="indskr_sortorder" />',
          '<attribute name="indskr_showinmenu" />',
          '<filter type="and">',
          '<condition attribute="statecode" operator="eq" value="0" />',
          '</filter>',
          // Ordering here is what puts each config's array in sort order: grouping preserves row
          // order, so no second sort is needed once the rows are bucketed.
          '<order attribute="indskr_sortorder" />',
          // The alias is load-bearing: without it Dynamics names the key after the intersect with
          // a positional suffix (indskr_agentskill_indskr_agentconfiguration1.…).
          '<link-entity name="indskr_agentskill_indskr_agentconfiguration" from="indskr_agentskillid" to="indskr_agentskillid" link-type="inner" intersect="true" alias="bind">',
          '<attribute name="indskr_agentconfigurationid" />',
          '</link-entity>',
        ]),
      );
      for (const r of rows ?? []) {
        const configId = r['bind.indskr_agentconfigurationid'];
        if (!configId) continue;
        const list = map.get(configId) ?? [];
        list.push({
          indskr_agentskillid: r.indskr_agentskillid,
          indskr_name: r.indskr_name ?? '',
          indskr_description: r.indskr_description ?? null,
          indskr_keywords: r.indskr_keywords ?? null,
          indskr_sortorder: r.indskr_sortorder ?? null,
          indskr_showinmenu: r.indskr_showinmenu ?? null,
        });
        map.set(configId, list);
      }
    } catch (e) {
      // indskr_agentskill is managed but its intersect is not, so an org without the N:N rejects
      // the join. Treat as "no skills" so config sync keeps working there.
      console.warn('AgentConfig skill fetch failed; treating all configs as skill-less:', e);
    }
    return map;
  }


  private static toAgentConfig(raw: RawAgentConfig): AgentConfig {
    return {
      id: raw.indskr_agentconfigurationid,
      // Foundry agent name (the endpoint URL segment) comes from indskr_agentname,
      // falling back to indskr_name when the dedicated column is empty.
      name: raw.indskr_agentname?.trim() || raw.indskr_name,
      endpoint: raw.indskr_endpoint,
      category: raw.indskr_agentcategory,
      host: raw.indskr_agenthost,
      dataSources: AgentConfigService.parseGroundingDeviceSources(raw.indskr_groundingconfig),
      groundingConfig: raw.indskr_groundingconfig?.trim() || undefined,
      instructions: raw.indskr_instructions?.trim() || undefined,
      model: raw.indskr_model?.trim() || undefined,
      additionalInstructions: raw.indskr_additionalinstructions?.trim() || undefined,
      dataverseMcpServerId: raw.indskr_dataversemcpserverid?.trim() || undefined,
      roleIds: raw.boundRoleIds?.length ? raw.boundRoleIds : undefined,
      skills: (raw.skills ?? []).map(AgentConfigService.toAgentSkill),
    };
  }

  private static toAgentSkill(raw: RawAgentSkill): AgentSkill {
    return {
      id: raw.indskr_agentskillid,
      name: raw.indskr_name,
      command: toSkillCommand(raw.indskr_name),
      description: raw.indskr_description?.trim() || undefined,
      keywords: raw.indskr_keywords?.trim() || undefined,
      sortOrder: raw.indskr_sortorder ?? undefined,
      showInMenu: !!raw.indskr_showinmenu,
    };
  }

  /**
   * indskr_groundingconfig holds the structured grounding JSON the PCF writes (a JSON string).
   * Its `sources: { kind, … }[]` array carries the data sources; the `kind: 'device'` entries are
   * the on-device picks, each `id` an @AIDataSource code. We return those codes verbatim.
   *
   * Empty-vs-absent is load-bearing for source resolution:
   *  - sources present → return the device-source codes (even [] when none were picked = configured-empty).
   *  - groundingConfig absent / unparseable / no sources array → undefined (fall back to the full
   *    kind set). Malformed JSON is treated as "not configured", never as "configured empty".
   */
  private static parseGroundingDeviceSources(raw: string | null | undefined): string[] | undefined {
    if (!raw) return undefined;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }
    const sources = (parsed as { sources?: unknown })?.sources;
    if (!Array.isArray(sources)) return undefined;
    return sources
      .filter((s): s is { kind: 'device'; id?: unknown } => (s as { kind?: unknown })?.kind === 'device')
      .map(s => s.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
  }
}
